import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import * as pricebookActions from './pricebook.actions';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { IPriceBook, IPriceBookSimple, IPriceBookViewDTO } from '../models/pricebook.model';
import { PricebookService } from '../services/pricebook.service';

@Injectable()
export class PricebooksEffects {
  constructor(private actions$: Actions, public store: Store<AppState>, private pricebookService: PricebookService) {}

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║                                                                    ║
   * ║                      Pricebook Effects                             ║
   * ║                                                                    ║
   * ║   All effects related to pricebook management in the application.   ║
   * ║   Includes loading pricebooks.                                     ║
   * ║                                                                    ║
   * ╚════════════════════════════════════════════════════════════════════╝
   */

  GetPricebooksEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.GetPricebooks),
      tap(() => {
        this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.pricebookService.getPricebooks(action.page, action.key).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.GetPricebooksSuccess({ pricebooks: res })),
          catchError(() => of(pricebookActions.GetPricebooksError())),
          finalize(() => {
            this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  AppendPricebooksEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.AppendPricebooks),
      switchMap((action) =>
        this.pricebookService.getPricebooks(action.page, action.key).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.AppendPricebooksSuccess({ pricebooks: res })),
          catchError(() => of(pricebookActions.AppendPricebooksError()))
        )
      )
    )
  );

  // Create Pricebook
  CreatePriceBookEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.CreatePriceBook),
      switchMap((action) =>
        this.pricebookService.create(action.pricebook).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.CreatePriceBookSuccess({ pricebook: res.data[0] })),
          catchError(() => of(pricebookActions.CreatePriceBookError()))
        )
      )
    )
  );

  // Update Pricebook
  UpdatePriceBookEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.UpdatePriceBook),
      switchMap((action) =>
        this.pricebookService.update(action.pricebook).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.UpdatePriceBookSuccess({ pricebook: res.data[0] })),
          catchError(() => of(pricebookActions.UpdatePriceBookError()))
        )
      )
    )
  );

  // Get Pricebook by Id
  GetPricebookByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.GetPricebookById),
      tap(() => this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: true }))),
      switchMap((action) =>
        this.pricebookService.getPricebookById(action.id).pipe(
          map((res: APIResponse<IPriceBookViewDTO>) => pricebookActions.GetPricebookByIdSuccess({ pricebook: res.data[0] })),
          catchError(() => of(pricebookActions.GetPricebookByIdError())),
          finalize(() => this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: false })))
        )
      )
    )
  );

  //delete pricebook
  DeletePricebookEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.DeletePricebook),
      tap(() => this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: true }))),
      switchMap((action) =>
        this.pricebookService.delete(action.id).pipe(
          map(() => pricebookActions.DeletePricebookSuccess({ id: action.id })),
          catchError(() => of(pricebookActions.DeletePricebookError())),
          finalize(() => this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: false })))
        )
      )
    )
  );

  // bulk delete pricebooks
  BulkDeletePricebooksEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.BulkDeletePricebooks),
      tap(() => this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: true }))),
      switchMap((action) =>
        this.pricebookService.bulkDelete(action.ids).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.BulkDeletePricebooksSuccess({ ids: action.ids })),
          catchError(() => of(pricebookActions.BulkDeletePricebooksError())),
          finalize(() => this.store.dispatch(pricebookActions.SetPricebookLoader({ isLoading: false })))
        )
      )
    )
  );

  EnablePricebooksEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.EnablePricebooks),
      switchMap((action) =>
        this.pricebookService.enable(action.ids).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.EnablePricebooksSuccess({ ids: action.ids })),
          catchError(() => of(pricebookActions.EnablePricebooksError()))
        )
      )
    )
  );

  DisablePricebooksEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.DisablePricebooks),
      switchMap((action) =>
        this.pricebookService.disable(action.ids).pipe(
          map((res: APIResponse<IPriceBookSimple>) => pricebookActions.DisablePricebooksSuccess({ ids: action.ids })),
          catchError(() => of(pricebookActions.DisablePricebooksError()))
        )
      )
    )
  );

  TogglePricebookForCatalogEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pricebookActions.TogglePricebookForCatalog),
      switchMap((action) =>
        this.pricebookService.togglePricebookForCatalog(action.catalogId, action.pricebookId).pipe(
          map(() => pricebookActions.TogglePricebookForCatalogSuccess({ catalogId: action.catalogId, pricebookId: action.pricebookId })),
          catchError(() => of(pricebookActions.TogglePricebookForCatalogError()))
        )
      )
    )
  );
}
